import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { VehicleNegotiationDTO } from 'src/app/core/interfaces/negotiation/vehicle-negotiation';
import { VehicleNegotiationInfoUser } from 'src/app/core/interfaces/negotiation/vehicle-negotiation-info-user';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { VehicleCargo } from 'src/app/core/interfaces/vehicle-cargo';
import { Patterns } from 'src/app/core/resources/patterns';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { VehiclesService } from 'src/app/modules/administration/vehicles/list-vehicles.service';
import { ManualCreationCargoService } from '../../manual-creation-cargo/manual-creation-cargo.service';
import { CatalogItem } from 'src/app/core/interfaces/catalogItem';
import { User } from 'src/app/core/interfaces/user';
import { AdminUsersService } from 'src/app/modules/administration/admin-users/admin-users.service';
import { NgxSpinnerService } from 'ngx-spinner';
@Component({
  selector: 'app-create-vehicle-application-dialog',
  templateUrl: './create-vehicle-application-dialog.component.html',
  styleUrls: ['./create-vehicle-application-dialog.component.scss']
})
export class CreateVehicleApplicationDialogComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  formValidate = '';
  subscriptions: Subscription = new Subscription();
  mode: 'create' | 'edit' = 'create';
  vehicles: CatalogItem[] = [];
  maxLoadCapacity = 99999;
  maxOffer = 99999999;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: {
      mode: 'create' | 'edit',
      negotiation?: VehicleNegotiationInfoUser,
      initialVehicleType?: string,
      initialBodyWorkType?: string,
      applicationList?: VehicleNegotiationInfoUser[]
    },
    private readonly dialogRef: MatDialogRef<CreateVehicleApplicationDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly snackBarService: SnackBarService,
    public readonly utils: Utils,
    public readonly patterns: Patterns,
    private readonly vehiclesService: VehiclesService,
    private readonly manualCreationCargoService: ManualCreationCargoService,
    private readonly adminUsersService: AdminUsersService,
    private readonly spinner: NgxSpinnerService
  ) {
    this.form = this.formBuilder.group({
      applicantId: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(15)]],
      licensePlate: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(8), Validators.pattern(this.patterns.GET_REGEX('LICENSE_PLATES').source)]],
      offer: [null, [Validators.required, Validators.min(1), Validators.max(this.maxOffer)]],
      phone: ['', [Validators.required, Validators.maxLength(12), Validators.minLength(7), Validators.pattern(this.patterns.PHONE.source)]],
      vehicleTypeName: ['', [Validators.required]],
      bodyWorkTypeName: ['', [Validators.required]],
      loadCapacity: [null, [Validators.required, Validators.min(1), Validators.max(this.maxLoadCapacity)]],
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.data && this.data.mode) this.mode = this.data.mode;
    this.spinner.show();
    await this.getVehicles();
    if (this.mode === 'create') this.setVehicleData(this.data.initialVehicleType, this.data.initialBodyWorkType);
    else if (this.data.negotiation) await this.setInitialValues();
    this.spinner.hide();
    this.setSubscriptions();
  }

  private async getVehicles(): Promise<void> {
    try {
      const data = await this.manualCreationCargoService.getVehicleType().toPromise();
      this.vehicles = data && data.catalog && data.catalog.length ? data.catalog : this.manualCreationCargoService.listVehicles;
    } catch (error) {
      this.vehicles = this.manualCreationCargoService.listVehicles;
    }
  }
  private async setInitialValues(): Promise<void> {
    const originalNegotiation = this.data.negotiation;
    const { vehicleFound, userFound } = await this.findVehicleByLicensePlate(originalNegotiation.licensePlate, true);
    if (!vehicleFound) {
      if (originalNegotiation.licensePlate) {
        this.form.get('licensePlate').setValue(originalNegotiation.licensePlate.toUpperCase());
        this.form.get('licensePlate').disable();
      }
      if (originalNegotiation.vehicleType)
        this.setVehicleData(originalNegotiation.vehicleType, originalNegotiation.bodyWorkType);
      if (originalNegotiation.loadCapacity)
        this.form.get('loadCapacity').setValue(originalNegotiation.loadCapacity);
      const { userFound: userFoundDetail } = await this.getDetailUserByDocument(originalNegotiation.applicantId, true);
      if (!userFoundDetail) {
        if (originalNegotiation.applicantId) this.form.get('applicantId').setValue(originalNegotiation.applicantId);
        if (originalNegotiation.phone) this.form.get('phone').setValue(originalNegotiation.phone);
      }
    } else if (!userFound) {
      if (originalNegotiation.applicantId) this.form.get('applicantId').setValue(originalNegotiation.applicantId);
      if (originalNegotiation.phone) this.form.get('phone').setValue(originalNegotiation.phone);
    }
    const initialValue = this.utils.getNestedValue(originalNegotiation, 'initialValue');
    const offer = this.utils.getNestedValue(originalNegotiation, 'offer');
    const finalValue = this.utils.getNestedValue(originalNegotiation, 'finalValue');
    const realOffer = finalValue ? finalValue : offer ? offer : initialValue ? initialValue : null;
    if (realOffer) this.form.get('offer').setValue(realOffer);

  }
  private setSubscriptions(): void {
    this.subscriptions.add(this.form.get('vehicleTypeName').valueChanges.subscribe(() => this.form.get('bodyWorkTypeName').setValue('')));
    this.subscriptions.add(this.form.get('licensePlate').valueChanges.pipe(distinctUntilChanged()).subscribe((licensePlate: string) => {
      if (licensePlate && licensePlate.trim().length >= 5 && licensePlate.trim().length <= 8)
        this.findVehicleByLicensePlate(licensePlate);
    }));
    this.subscriptions.add(this.form.get('applicantId').valueChanges.pipe(distinctUntilChanged()).subscribe((applicantId: string) => {
      if (applicantId && applicantId.trim().length >= 7 && applicantId.trim().length <= 15)
        this.getDetailUserByDocument(applicantId);
    }));
  }

  private async findVehicleByLicensePlate(licensePlate: string, disable: boolean = false): Promise<{ vehicleFound: boolean, userFound: boolean }> {
    if (!licensePlate || !licensePlate.trim()) return { vehicleFound: false, userFound: false };
    try {
      const vehicle = await this.vehiclesService.getVehicleFromOwnCompany(licensePlate).toPromise();
      if (!vehicle || !vehicle.id) return { vehicleFound: false, userFound: false };
      this.form.get('licensePlate').setValue(licensePlate.toUpperCase());
      if (disable) this.form.get('licensePlate').disable();
      const vehicleTypeName = this.utils.getNestedValue(vehicle, 'vehicleType.name');
      const bodyWorkTypeName = this.utils.getNestedValue(vehicle, 'bodywork.description');
      this.setVehicleData(vehicleTypeName, bodyWorkTypeName, disable);
      const capacity = this.utils.getNestedValue(vehicle, 'capacity');
      if (capacity && Number(capacity) > 0) {
        this.form.get('loadCapacity').setValue(Number(capacity));
        if (disable) this.form.get('loadCapacity').disable();
      }
      const driverDocument = this.utils.getNestedValue(vehicle, 'driver.document');
      if (driverDocument) {
        const { userFound } = await this.getDetailUserByDocument(driverDocument, disable);
        if (userFound) return { vehicleFound: true, userFound: true };
        this.form.get('applicantId').setValue(driverDocument);
        if (disable) this.form.get('applicantId').disable();
        return { vehicleFound: true, userFound: false };
      }
      return { vehicleFound: true, userFound: false };
    } catch (error) { return { vehicleFound: false, userFound: false }; }
  }
  private setVehicleData(vehicleTypeName: string, bodyWorkTypeName: string, disable: boolean = false): void {
    const realVehicleType = this.vehicles.find(vehicle => vehicle.name === vehicleTypeName);
    if (realVehicleType) {
      this.form.get('vehicleTypeName').setValue({ name: realVehicleType.name, id: realVehicleType.id });
      if (bodyWorkTypeName) this.form.get('bodyWorkTypeName').setValue({ name: bodyWorkTypeName });
    }
    if (disable) {
      this.form.get('vehicleTypeName').disable();
      this.form.get('bodyWorkTypeName').disable();
      this.formValidate = 'disable';
    }
  }
  private async getDetailUserByDocument(userDocument: string, disable: boolean = false): Promise<{ userFound: boolean }> {
    if (!userDocument || !userDocument.trim()) return { userFound: false };
    try {
      const user = await this.adminUsersService.getUsersDetailByDocument(userDocument).toPromise();
      if (!user || !user.information || !user.information.document) return { userFound: false };
      this.form.get('applicantId').setValue(user.information.document);
      if (disable) this.form.get('applicantId').disable();
      if (user.phone) {
        this.form.get('phone').setValue(user.phone);
        if (disable) this.form.get('phone').disable();
      }
      return { userFound: true };
    } catch (error) { return { userFound: false }; }
  }



  public close(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    this.form.markAllAsTouched();
    this.formValidate = 'touched';
    this.trimValues();
    if (this.utils.errorMessagesCustomized(this.form.get('licensePlate'), 'placa', 5, 8)) return;
    if (this.utils.errorMessagesCustomized(this.form.get('applicantId'), 'documento del conductor', 7, 15)) return;
    if (this.utils.errorMessagesCustomized(this.form.get('phone'), 'cel. del conductor', 7, 12)) return;
    if (this.utils.errorMessagesCustomized(this.form.get('vehicleTypeName'), 'tipo de vehículo')) return;
    if (this.utils.errorMessagesCustomized(this.form.get('bodyWorkTypeName'), 'tipo de carrocería')) return;
    if (this.utils.errorMessagesCustomized(this.form.get('loadCapacity'), 'capacidad de carga', null, null, 1, this.maxLoadCapacity)) return;
    if (this.utils.errorMessagesCustomized(this.form.get('offer'), 'oferta', null, null, 1, this.maxOffer)) return;
    if (this.form.invalid) return this.snackBarService.openSnackBar('Hay errores en el formulario', undefined, 'alert');

    if (this.data.applicationList && this.data.applicationList.length) {
      const applicationList = this.data.applicationList;
      const existingApplication = applicationList.find(application => {
        if (this.mode === 'create') return application.licensePlate === this.form.get('licensePlate').value;
        return application.licensePlate === this.form.get('licensePlate').value && application.id !== this.data.negotiation.id;
      });
      if (existingApplication) return this.snackBarService.openSnackBar('La placa ya tiene una postulación', undefined, 'alert');
    }
    const negotiationPayload = this.getNegotiationPayload();
    this.dialogRef.close({
      state: true,
      negotiation: negotiationPayload
    });
  }

  private trimValues(): void {
    ['applicantId', 'licensePlate', 'phone'].forEach((controlName: string) => {
      const value = this.form.get(controlName).value;
      if (typeof value === 'string') this.form.get(controlName).setValue(value.trim());
    });
  }

  private getNegotiationPayload(): VehicleNegotiationDTO {
    const vehicleTypeName = this.form.get('vehicleTypeName').value;
    const bodyWorkTypeName = this.form.get('bodyWorkTypeName').value;
    return {
      applicantId: this.form.get('applicantId').value,
      licensePlate: String(this.form.get('licensePlate').value || '').toUpperCase(),
      initialValue: Number(this.form.get('offer').value),
      offer: Number(this.form.get('offer').value),
      phone: this.form.get('phone').value,
      vehicleType: vehicleTypeName.name,
      bodyWorkType: bodyWorkTypeName.name,
      loadCapacity: this.form.get('loadCapacity').value ? Number(this.form.get('loadCapacity').value) : undefined
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
